export interface INerisFeatureFlags {
  allow_ui_incident_creation: boolean;
  allow_ui_no_activity_report_creation: boolean;
}

export interface INerisPopulation {
  protected: number;
  source: string;
}

export interface INerisDispatch {
  avl_usage: boolean;
  center_id: string;
  cad_software: string;
  psap_type: string;
  psap_capability: string;
  psap_discipline: string;
  psap_jurisdiction: string;
  protocol_fire: string;
  protocol_med: string;
}

export interface INerisStaffing {
  active_firefighters_career_ft: number;
  active_firefighters_career_pt: number;
  active_firefighters_volunteer: number;
  active_ems_only_career_ft: number;
  active_ems_only_career_pt: number;
  active_ems_only_volunteer: number;
  active_civilians_career_ft: number;
  active_civilians_career_pt: number;
  active_civilians_volunteer: number;
}

export interface INerisAssessment {
  iso_rating: number;
  cpse_accredited: boolean;
  caas_accredited: boolean;
}

export interface INerisShift {
  count: number;
  duration: number;
  signup: number;
}

export interface INerisUnit {
  staffing: number;
  dedicated_staffing: boolean;
  neris_id: string;
  version: number;
  type: string;
  cad_designation_1: string;
  cad_designation_2: string;
}

export interface INerisStation {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  station_id: string;
  internal_id: string;
  staffing: number;
  neris_id: string;
  version: number;
  units: INerisUnit[];
  location: string;
}

export interface INerisRegion {
  name: string;
  internal_id: string;
  url: string;
}

export interface INerisRegionSet {
  name: string;
  primary: boolean;
  coverage: boolean;
  juris: boolean;
  type: string;
  regions: INerisRegion[];
}

export interface INerisEntity {
  feature_flags: INerisFeatureFlags;
  mail_address_line_1: string;
  mail_address_line_2: string;
  mail_city: string;
  mail_state: string;
  mail_zip_code: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  name: string;
  email: string;
  continue_edu: boolean;
  internal_id: string;
  rms_software: string;
  time_zone: string;
  neris_id: string;
  version: number;
  last_modified: string;
  onboarding_status: string;
  department_type: string;
  entity_type: string;
  population: INerisPopulation;
  dispatch: INerisDispatch;
  staffing: INerisStaffing;
  assessment: INerisAssessment;
  website: string;
  shift: INerisShift;
  fire_services: string[];
  ems_services: string[];
  investigation_services: string[];
  stations: INerisStation[];
  location: string;
  region_sets: INerisRegionSet[];
}

export enum NerisEnrollmentStatus {
  ENROLLED = 'ENROLLED',
  NOT_ENROLLED = 'NOT_ENROLLED',
  CHECKING = 'CHECKING',
  NONE = 'NONE',
  OTHER_ACCOUNT_USING_THIS_ENTITY_ID = 'OTHER_ACCOUNT_USING_THIS_ENTITY_ID',
  SAME_ACCOUNT_USING_THIS_ENTITY_ID = 'SAME_ACCOUNT_USING_THIS_ENTITY_ID',
}

/**
 * Entity Response Types
 */
export interface EntityFeatureFlags {
  allow_ui_incident_creation: boolean;
  allow_ui_no_activity_report_creation: boolean;
}

export interface EntityPopulation {
  protected: number;
  source: string;
}

export interface EntityDispatch {
  avl_usage: boolean;
  center_id: string;
  cad_software: string;
  psap_type: string;
  psap_capability: string;
  psap_discipline: string;
  psap_jurisdiction: string;
  protocol_fire: string;
  protocol_med: string;
}

export interface EntityStaffing {
  active_firefighters_career_ft: number;
  active_firefighters_career_pt: number;
  active_firefighters_volunteer: number;
  active_ems_only_career_ft: number;
  active_ems_only_career_pt: number;
  active_ems_only_volunteer: number;
  active_civilians_career_ft: number;
  active_civilians_career_pt: number;
  active_civilians_volunteer: number;
}

export interface EntityAssessment {
  iso_rating: number;
  cpse_accredited: boolean;
  caas_accredited: boolean;
}

export interface EntityShift {
  count: number;
  duration: number;
  signup: number;
}

export interface EntityUnit {
  staffing: number;
  dedicated_staffing: boolean;
  neris_id: string;
  version: number;
  type: string;
  cad_designation_1: string;
  cad_designation_2: string;
}

export interface EntityStation {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  station_id: string;
  internal_id: string;
  staffing: number;
  neris_id: string;
  version: number;
  units: EntityUnit[];
  location: string;
}

export interface EntityRegion {
  name: string;
  internal_id: string;
  url: string;
}

export interface EntityRegionSet {
  name: string;
  primary: boolean;
  coverage: boolean;
  juris: boolean;
  type: string;
  regions: EntityRegion[];
}

export type StatesTerrs =
  | 'AK'
  | 'AL'
  | 'AR'
  | 'AS'
  | 'AZ'
  | 'CA'
  | 'CO'
  | 'CT'
  | 'DC'
  | 'DE'
  | 'FL'
  | 'FM'
  | 'GA'
  | 'GU'
  | 'HI'
  | 'IA'
  | 'ID'
  | 'IL'
  | 'IN'
  | 'KS'
  | 'KY'
  | 'LA'
  | 'MA'
  | 'MD'
  | 'ME'
  | 'MH'
  | 'MI'
  | 'MN'
  | 'MO'
  | 'MP'
  | 'MS'
  | 'MT'
  | 'NA'
  | 'NC'
  | 'ND'
  | 'NE'
  | 'NH'
  | 'NJ'
  | 'NM'
  | 'NV'
  | 'NY'
  | 'OH'
  | 'OK'
  | 'OR'
  | 'PA'
  | 'PR'
  | 'PW'
  | 'RI'
  | 'SC'
  | 'SD'
  | 'TN'
  | 'TX'
  | 'UM'
  | 'UT'
  | 'VA'
  | 'VI'
  | 'VT'
  | 'WA'
  | 'WI'
  | 'WV'
  | 'WY';

export type DeptOnboardingStatus =
  | 'UNCONFIRMED'
  | 'CONFIRMED'
  | 'PENDING'
  | 'REJECTED';

/**
 * Entity Response
 *
 * Complete entity object returned from NERIS API
 */
export interface EntityResponse {
  feature_flags: EntityFeatureFlags;
  mail_address_line_1: string;
  mail_address_line_2: string;
  mail_city: string;
  mail_state: StatesTerrs;
  mail_zip_code: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: StatesTerrs;
  zip_code: string;
  name: string;
  email: string;
  continue_edu: boolean;
  internal_id: string;
  rms_software: string;
  time_zone: string;
  neris_id: string;
  version: number;
  last_modified: string;
  onboarding_status: DeptOnboardingStatus;
  department_type: string;
  entity_type: string;
  population: EntityPopulation;
  dispatch: EntityDispatch;
  staffing: EntityStaffing;
  assessment: EntityAssessment;
  website: string;
  shift: EntityShift;
  fire_services: string[];
  ems_services: string[];
  investigation_services: string[];
  stations: EntityStation[];
  location: string;
  region_sets: EntityRegionSet[];
}

export interface NerisCheckEnrollmentResponse {
  status: NerisEnrollmentStatus;
  entity: EntityResponse;
  message: string;
}
